import type { Order } from "./types";
import { graphQlClient } from "@lib/wrappers/graphqlWrapper";
import type { GetMultipleProductVariantsBySkusQuery } from "@graphql/generated/components";
import { LineItemStatus, LineType } from "./types";
import { GetMultipleProductVariantsBySkusDocument } from "@graphql/generated/components";
import { getCurrencyForSiteByLocale } from "@config/site/site-config";
import { GIFT_WRAPPING_KEY, GIFT_WRAPPING_SKU } from "@lib/constants/product";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { Attributes, CustomAttributes } from "@lib/enums/ProductAttributes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

export const addProductDetailsToOrders = async (orders: Order[], locale: string) => {
    if (!orders || orders.length === 0) return null;

    const productIds = orders.map((order) => order.lines.map((line) => line.productId)).flat();
    const productsDetails = await getProductDetailsForProductIds(productIds, locale);
    orders?.forEach((order) => {
        order?.lines?.forEach((line) => {
            if (productsDetails) {
                const details = productsDetails[line.productId];
                if (details) {
                    line.variant = { ...details };
                    line.productKey = details.sku;
                    line.name = getProductAttributeValue(
                        details.attributesRaw,
                        Attributes.productNameCustomerFriendly,
                        locale
                    );
                    // @ts-ignore
                    line.custom = {
                        customFieldsRaw: [
                            {
                                name: CustomAttributes.LINE_ITEM_PRICE,
                                value: line.itemListPrice,
                            },
                            ...(line.itemGrossPrice.centAmount !== line.itemListPrice.centAmount &&
                            line.itemGrossPrice.centAmount !== 0 &&
                            line.itemGrossPrice.centAmount !== undefined &&
                            line.itemGrossPrice.centAmount !== null
                                ? [
                                      {
                                          name: CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE,
                                          value: line.itemGrossPrice,
                                      },
                                  ]
                                : []),
                        ],
                    };
                }
            }
            line.isReturn =
                line?.lineType === LineType.return &&
                (line?.itemStatus === LineItemStatus.shipped ||
                    line?.itemStatus === LineItemStatus.complete);

            if (line?.isReturn) {
                line.itemStatus = LineItemStatus.returned;
            }
        });
    });
    return orders;
};

const getProductDetailsForProductIds = async (productIds: string[], locale: string) => {
    try {
        const { data: commercetoolsData, error: commercetoolsError } =
            await graphQlClient.queryCommerceTools<GetMultipleProductVariantsBySkusQuery>(
                GetMultipleProductVariantsBySkusDocument,
                {
                    locale: locale,
                    skus: productIds,
                    currency: getCurrencyForSiteByLocale(locale),
                }
            );
        if (commercetoolsError) {
            return null;
        }

        const productDetails: { [sku: string]: any } = {};

        commercetoolsData?.products?.results?.forEach((product) => {
            if (product.key === GIFT_WRAPPING_KEY) {
                productDetails[GIFT_WRAPPING_SKU] = product.masterData.current;
            }
            product?.masterData?.current?.variants?.forEach((variant) => {
                variant.attributesRaw.forEach((attribute) => {
                    if (typeof attribute.value === "object" && attribute.value !== null) {
                        // Check if the value is an object with locale keys
                        const localizedValue = attribute.value[locale];
                        if (localizedValue) {
                            // Retain only the value corresponding to the specified locale
                            attribute.value = { [locale]: localizedValue };
                        }
                    }
                });
                // Add the variant to the product details object using the SKU as the key
                productDetails[variant.sku] = variant;
            });
        });

        return productDetails;
    } catch (error) {
        Logger.error(ServiceType.COMMERCE_TOOLS, "Error fetching product details", {
            tag: LogTag.ORDER_HISTORY,
            message: "Failed to fetch product details from CommerceTools",
        });
        return null;
    }
};
